<template>
  <aside class="pr-sidebar">
    <RouterLink class="pr-logo" to="/dashboard" aria-label="Price Rescue dashboard">
      <span class="pr-logo-mark">P</span>
      <span>PriceRescue</span>
    </RouterLink>
    <nav class="pr-nav" aria-label="Merchant navigation">
      <div v-for="item in navigationItems" :key="item.id" class="pr-nav-group">
        <button
          v-if="item.children !== undefined"
          class="pr-nav-link pr-nav-toggle"
          :class="{ active: itemIsActive(item) }"
          type="button"
          :aria-expanded="groupIsExpanded(item.id)"
          @click="activateGroup(item)"
        >
          <MerchantSidebarIcon :nav-id="item.id" />
          <span>{{ labelForItem(item) }}</span>
          <span class="pr-nav-caret" aria-hidden="true">›</span>
        </button>
        <RouterLink
          v-else
          class="pr-nav-link"
          :class="{ active: itemIsActive(item) }"
          :to="item.path"
        >
          <MerchantSidebarIcon :nav-id="item.id" />
          <span>{{ labelForItem(item) }}</span>
        </RouterLink>
        <div v-if="item.children !== undefined && groupIsExpanded(item.id)" class="pr-nav-children">
          <RouterLink
            v-for="child in item.children"
            :key="child.id"
            class="pr-nav-child-link"
            :class="{ active: child.id === activeNavId }"
            :to="child.path"
          >
            {{ labelForItem(child) }}
          </RouterLink>
        </div>
      </div>
    </nav>
    <div class="pr-store-switch">
      <div class="pr-shop-icon">S</div>
      <div>
        <b>{{ storeName }}</b>
        <small>{{ shopDomain }}</small>
      </div>
    </div>
  </aside>
</template>

<script setup lang="ts">
import { computed, defineComponent, h, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

import { ROUTES, visibleNavigation } from "../../navigation.js";
import { useMerchantSessionStore } from "../../stores/session-store.js";
import type { NavigationItem } from "../../types.js";

const route = useRoute();
const router = useRouter();
const session = useMerchantSessionStore();

const approvedNavOrder = [
  "dashboard",
  "requests",
  "catalog-pricing",
  "store-configuration",
  "administration",
  "support-feedback",
  "faq",
];

const activeNavId = computed(() => {
  if (route.meta["navId"] === "settings" && route.query.tab === "notifications") {
    return "notifications-sla";
  }
  return String(route.meta["navId"] ?? "dashboard");
});
const navigationItems = computed(() =>
  orderedMockNavigation(
    session.sessionContext === undefined ? ROUTES : visibleNavigation(session.sessionContext),
  ),
);
const storeName = computed(() =>
  shopName(
    session.sessionContext?.store.displayName,
    session.sessionContext?.store.name,
    session.sessionContext?.store.shopDomain,
  ),
);
const shopDomain = computed(
  () => session.sessionContext?.store.shopDomain ?? "my-demo-store.example.com",
);
const expandedGroupIds = ref(new Set<string>());

watch(activeNavId, expandActiveGroup, { immediate: true });

const MerchantSidebarIcon = defineComponent({
  props: {
    navId: { type: String, required: true },
  },
  setup(props) {
    return () =>
      h(
        "svg",
        {
          class: "pr-nav-icon",
          viewBox: "0 0 24 24",
          width: "18",
          height: "18",
          "aria-hidden": "true",
        },
        iconChildren(props.navId),
      );
  },
});

function orderedMockNavigation(items: NavigationItem[]): NavigationItem[] {
  const byId = new Map(items.map((item) => [item.id, item]));
  return approvedNavOrder.flatMap((id) => {
    const item = byId.get(id);
    return item === undefined ? [] : [item];
  });
}

function itemIsActive(item: NavigationItem): boolean {
  return (
    item.id === activeNavId.value ||
    item.children?.some((child) => child.id === activeNavId.value) === true
  );
}

function groupIsExpanded(groupId: string): boolean {
  return expandedGroupIds.value.has(groupId);
}

function toggleGroup(groupId: string): void {
  const next = new Set(expandedGroupIds.value);
  if (next.has(groupId)) {
    next.delete(groupId);
  } else {
    next.add(groupId);
  }
  expandedGroupIds.value = next;
}

function activateGroup(item: NavigationItem): void {
  toggleGroup(item.id);
  if (route.fullPath === item.path) return;
  void router.push(item.path);
}

function expandActiveGroup(): void {
  const activeGroup = navigationItems.value.find(
    (item) => item.children?.some((child) => child.id === activeNavId.value) === true,
  );
  if (activeGroup === undefined || expandedGroupIds.value.has(activeGroup.id)) return;
  expandedGroupIds.value = new Set([...expandedGroupIds.value, activeGroup.id]);
}

function labelForItem(item: NavigationItem): string {
  if (item.id === "reports") return "Analytics & Reports";
  return item.label;
}

function shopName(
  displayName: string | undefined,
  name: string | undefined,
  domain: string | undefined,
): string {
  if (isUsableDisplayName(displayName, domain)) return displayName.trim();
  if (isUsableDisplayName(name, domain)) return name.trim();
  return "PR Demo Store";
}

function isUsableDisplayName(
  value: string | undefined,
  domain: string | undefined,
): value is string {
  return value !== undefined && value.trim().length > 0 && value !== domain;
}

function iconChildren(navId: string) {
  switch (navId) {
    case "dashboard":
      return [filledPath("M3 13h8V3H3v10Zm10 8h8V11h-8v10ZM3 21h8v-6H3v6Zm10-12h8V3h-8v6Z")];
    case "requests":
      return [
        outlinePath(
          "M6 3h12a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Zm2 5h8M8 12h8M8 16h5",
        ),
      ];
    case "products":
      return [outlinePath("m12 2 9 5-9 5-9-5 9-5Zm9 5v10l-9 5-9-5V7m9 5v10")];
    case "competitors":
      return [
        outlinePath(
          "M7 4a3 3 0 1 0 0 6 3 3 0 0 0 0-6Zm10 10a3 3 0 1 0 0 6 3 3 0 0 0 0-6ZM9.5 9.5 14.5 14.5M14 5h5v5M10 19H5v-5",
        ),
      ];
    case "catalog-pricing":
      return [
        outlinePath("m12 2 9 5-9 5-9-5 9-5Zm9 5v10l-9 5-9-5V7m9 5v10"),
        outlinePath("M7 15h4M13 17h4"),
      ];
    case "store-configuration":
      return [
        outlinePath(
          "M12 2a4 4 0 0 0-4 4v1H6a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-2V6a4 4 0 0 0-4-4Zm-2 5V6a2 2 0 1 1 4 0v1h-4Z",
        ),
      ];
    case "mappings":
      return [outlinePath("M8 7h8M8 17h8M6 4l-3 3 3 3M18 14l3 3-3 3")];
    case "policies":
      return [
        outlinePath("M12 2 20 6v6c0 5-3.5 8.5-8 10-4.5-1.5-8-5-8-10V6l8-4Z"),
        outlinePath("m9 12 2 2 4-5"),
      ];
    case "operations":
      return [outlinePath("M4 6h16M4 12h16M4 18h16"), outlinePath("M8 6v12M16 6v12")];
    case "monitoring":
      return [outlinePath("M3 12h4l2-7 4 14 2-7h6")];
    case "reports":
      return [outlinePath("M4 20V10M10 20V4M16 20v-7M22 20H2")];
    case "team":
      return [
        outlinePath(
          "M9 4a4 4 0 1 0 0 8 4 4 0 0 0 0-8Zm9 2a3 3 0 1 0 0 6 3 3 0 0 0 0-6ZM2 21c0-5 3-8 7-8s7 3 7 8M15 14c4 0 7 2 7 6",
        ),
      ];
    case "administration":
      return [
        outlinePath("M12 3 4 6v6c0 5 3.5 8 8 9 4.5-1 8-4 8-9V6l-8-3Z"),
        outlinePath("M9 12l2 2 4-5"),
      ];
    case "audit":
      return [outlinePath("M6 2h9l4 4v16H6V2Zm9 0v5h5M9 12h7M9 16h7M9 8h2")];
    case "settings":
      return [
        outlinePath(
          "M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6Zm7.4 6a1.7 1.7 0 0 0 .3 1.9l.1.1-2.8 2.8-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.6v.2h-4V21a1.7 1.7 0 0 0-1-1.6 1.7 1.7 0 0 0-1.9.3l-.1.1L4.2 17l.1-.1A1.7 1.7 0 0 0 4.6 15 1.7 1.7 0 0 0 3 14H2.8v-4H3a1.7 1.7 0 0 0 1.6-1 1.7 1.7 0 0 0-.3-1.9l-.1-.1L7 4.2l.1.1A1.7 1.7 0 0 0 9 4.6a1.7 1.7 0 0 0 1-1.6v-.2h4V3a1.7 1.7 0 0 0 1 1.6 1.7 1.7 0 0 0 1.9-.3l.1-.1L19.8 7l-.1.1a1.7 1.7 0 0 0-.3 1.9 1.7 1.7 0 0 0 1.6 1h.2v4H21a1.7 1.7 0 0 0-1.6 1Z",
        ),
      ];
    case "support-feedback":
      return [
        outlinePath(
          "M20 11.5a7.5 7.5 0 0 1-8 7.5 8.5 8.5 0 0 1-3-.5L4 20l1.5-4A7.5 7.5 0 1 1 20 11.5Z",
        ),
      ];
    case "faq":
      return [
        outlinePath("M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18Z"),
        outlinePath("M9.5 9a2.5 2.5 0 1 1 4.4 1.6c-1 .9-1.9 1.4-1.9 2.9M12 17h.01"),
      ];
    default:
      return [outlinePath("M4 12h16M12 4v16")];
  }
}

function filledPath(d: string) {
  return h("path", { d, fill: "currentColor" });
}

function outlinePath(d: string) {
  return h("path", {
    d,
    fill: "none",
    stroke: "currentColor",
    "stroke-width": "2",
    "stroke-linecap": "round",
    "stroke-linejoin": "round",
  });
}
</script>
